import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { ExamSurfaceDisclaimer } from '@/components/ExamSurfaceDisclaimer';
import { Card } from '@/components/ui/Card';
import { SelectField } from '@/components/ui/Field';
import { PageHeader } from '@/components/ui/PageHeader';
import { LoadingLine } from '@/components/ui/States';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import {
  AdminApiError,
  createGeneration,
  fetchTopicSeeds,
} from '@/features/admin-content/admin-api';
import {
  NewGenerationForm,
  type NewGenerationFormValues,
} from '@/features/admin-content/NewGenerationForm';
import { useCan, useCurrentUser } from '@/features/admin-content/use-current-user';
import { BRAND } from '@/lib/brand';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/content/new')({
  component: AdminContentNewPage,
});

function AdminContentNewPage() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const canGenerate = useCan('content.generate');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [skill, setSkill] = useState<'reading' | 'listening' | 'writing' | 'speaking'>('reading');
  const seedsRef = `ielts-academic-${skill}.json`;

  const examsQuery = useQuery({
    queryKey: ['exams'],
    enabled: canGenerate,
    queryFn: () => api.get('/api/v1/exams'),
  });

  const seedsQuery = useQuery({
    queryKey: ['admin', 'topic-seeds', seedsRef],
    enabled: canGenerate,
    queryFn: () => fetchTopicSeeds(seedsRef),
  });

  const createMutation = useMutation({
    mutationFn: (values: NewGenerationFormValues) =>
      createGeneration({
        variant: values.variant,
        skill,
        ...(values.seedsMode === 'ref'
          ? { seedsRef: values.seedsRef }
          : { inlineSeeds: values.inlineSeeds }),
        budgetUsd: values.budgetUsd,
      }),
    onSuccess: (result) => {
      void navigate({ to: '/admin/content/$generationId', params: { generationId: result.id } });
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof AdminApiError
          ? 'We could not start the run.'
          : 'Something went wrong. Try again.',
      );
    },
  });

  if (sessionPending || meQuery.isPending) {
    return <LoadingLine>Loading</LoadingLine>;
  }

  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }

  if (!canGenerate) {
    return (
      <StaffOnly
        title="You cannot start a run"
        description="Teachers review content. Only an admin can start a generation run."
      />
    );
  }

  const variants = examsQuery.data?.exams.flatMap((exam) =>
    exam.variants.map((variant) => variant.slug),
  ) ?? ['ielts-academic'];
  const defaultVariant = variants.includes('ielts-academic')
    ? 'ielts-academic'
    : (variants[0] ?? 'ielts-academic');

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Workspace"
        title="Start content generation"
        description="Reading runs use the same gates and import path as the offline script. Listening and Writing runs make Drafts for someone to review."
      />

      <ExamSurfaceDisclaimer text={BRAND.disclaimer} />

      <Card padding="lg" className="grid gap-4">
        <SelectField
          label="Skill"
          value={skill}
          onChange={(event) => {
            setSkill(event.target.value as typeof skill);
            setErrorMessage(null);
          }}
        >
          <option value="reading">Reading</option>
          <option value="listening">Listening (script and questions)</option>
          <option value="writing">Writing (Task 1 and Task 2 prompts)</option>
          <option value="speaking">Speaking (Part 1, cue card, Part 3)</option>
        </SelectField>
        {seedsQuery.isPending ? (
          <LoadingLine>Loading the default seeds</LoadingLine>
        ) : (
          <NewGenerationForm
            key={skill}
            defaultVariant={defaultVariant}
            defaultSeedsRef={seedsRef}
            defaultInlineSeeds={seedsQuery.data?.sets.slice(0, 3) ?? []}
            variants={variants}
            onSubmit={(values) => {
              setErrorMessage(null);
              createMutation.mutate(values);
            }}
            isSubmitting={createMutation.isPending}
            errorMessage={errorMessage}
          />
        )}
      </Card>
    </div>
  );
}
